/**
 * D-EZ-107 — HOW EACH KIND OF MAN SAYS IT.
 *
 * Six voices × fourteen facts. Every line obeys the paper's quote rules
 * (`paper/quoteBank.ts`) and two more of its own, and `quotes.test.ts` holds
 * every line to them:
 *
 *   • NO DIGITS, NO NAMES, NO RESULTS of their own. A number or a name can only
 *     come in through a slot — `{other}` (the other man in the fact, by his
 *     surname), `{sponsor}`, `{team}`, `{n}` / `{nth}` (a milestone's count) —
 *     and a line whose slot the fact does not fill is never chosen.
 *   • NOTHING THE FACT DOES NOT SAY. A line for "wrecked by" may say he got
 *     into me; it may not say it was the last lap, or that it cost the title.
 *
 * Tags are short add-ons by mood ('up' / 'down' / 'mixed'), and RIVAL tags are
 * only used when the rivalry book shows the two men have history before this
 * race — "not the first time" is then a fact, not a flourish.
 */

import type { Voice } from './personality.ts';

export type FactKind =
  | 'won'
  | 'won-first'
  | 'won-milestone'
  | 'second'
  | 'good-run'
  | 'bad-day'
  | 'wrecked-by'
  | 'wrecked'
  | 'lost-lead-late'
  | 'dnf-mechanical'
  | 'clinched'
  | 'eliminated'
  | 'rival-nearby'
  | 'rival-won'
  | 'injury-return'
  | 'milestone-starts';

export type Mood = 'up' | 'down' | 'mixed';

export const MOOD: Record<FactKind, Mood> = {
  won: 'up',
  'won-first': 'up',
  'won-milestone': 'up',
  second: 'mixed',
  'good-run': 'up',
  'bad-day': 'down',
  'wrecked-by': 'down',
  wrecked: 'mixed',
  'lost-lead-late': 'down',
  'dnf-mechanical': 'down',
  clinched: 'up',
  eliminated: 'down',
  'rival-nearby': 'mixed',
  'rival-won': 'down',
  'injury-return': 'up',
  'milestone-starts': 'up',
};

type Bank = Record<FactKind, readonly string[]>;

const HOTHEAD: Bank = {
  won: [
    'Y’all can write whatever you want now. We won the dang thing.',
    'I told everybody in that garage this car could win. Nobody listened.',
    'That’s what happens when you drive it like you stole it.',
    'I’ve been mad all week and I took it out on the race track.',
    'Hard racing out there and I gave back every bit I got.',
    'I wasn’t about to lift for anybody today.',
    'Put it in the paper — this bunch can flat run.',
    'You want to win in this series, you take it. Nobody hands it to you.',
  ],
  'won-first': [
    'Everybody said I’d never win one of these. Look at it now.',
    'First one, and it won’t be the last. Write that down.',
    'All those years of people telling me to go home. Well, here I am.',
    'I’ve wanted this so bad I could taste it, and today I took it.',
    'Somebody pinch me — no, don’t, I’ll swing at you.',
  ],
  'won-milestone': [
    'Win number {n}, and I’m still hungry.',
    'That’s {n} of ’em. I want a whole lot more.',
    'They’ll have to drag me out of this car before I quit at {n}.',
  ],
  second: [
    'Second don’t pay the bills I want paid.',
    '{other} got the trophy, I got the leftovers. I hate it.',
    'Close only counts in horseshoes.',
    'I’ll be stewing on that one all the way home.',
    'Ain’t nobody remembers second, and I sure won’t want to.',
    'We had something for {other}, just not enough. Next time.',
  ],
  'good-run': [
    'Decent day, but decent ain’t what I came for.',
    'We ran up front and I’m still not happy. That’s how it is.',
    'Good points. I wanted the trophy.',
    'Car was good. I’ll be better next week.',
    'Can’t complain much. I’ll find a way, though.',
  ],
  'bad-day': [
    'That was a long, miserable afternoon and I don’t want to talk about it.',
    'We were junk today. Plain junk.',
    'I’ve had better days at the dentist.',
    'Get me out of here before I say something.',
    'Nothing went right. Nothing.',
  ],
  'wrecked-by': [
    'He ran into me, plain and simple.',
    '{other} wrecked me and he knows it.',
    'I’ve got a long memory, and {other} just made the list.',
    'You don’t race people like that. Not me, you don’t.',
    'He had all the room in the world and he chose me.',
    'I’ll see {other} down the road. That’s all I’m saying.',
    'If that’s how {other} wants to race, fine. Two can play.',
  ],
  wrecked: [
    'He was in my way. I wasn’t going to wait all day.',
    'That’s hard racing. If {other} can’t take it, stay home.',
    'I didn’t go out there to make friends.',
    'Rubbing is racing. Somebody tell {other}.',
    'I’m not apologizing for racing for a win.',
    'He’ll be mad. He can be mad.',
  ],
  'lost-lead-late': [
    'We had it won. Had it won! And it got away.',
    'That one’s going to sit in my gut all week.',
    'Somebody owes me a trophy.',
    'I could kick something right now.',
    'Leading late and not leading at the end. Makes me sick.',
  ],
  'dnf-mechanical': [
    'Something broke and so did my mood.',
    'You can’t win a race parked in the garage.',
    'I want to know what broke and I want to know today.',
    'We were in the hunt and the car quit on us. Unbelievable.',
    'I’m about ready to take a hammer to that thing.',
  ],
  clinched: [
    'They said we couldn’t do it. We did it. End of story.',
    'Nobody gave us anything. We took every inch of it.',
    'Mark it down — we’re in, and we’re coming.',
    'That’s for everybody who doubted this team.',
  ],
  eliminated: [
    'This stinks and I’m not going to pretend it doesn’t.',
    'We gave it away and that’s on all of us.',
    'Don’t ask me how I feel. You know how I feel.',
    'I’ll be mad about this one all winter.',
  ],
  'rival-nearby': [
    'I know right where {other} is. Let him worry about me.',
    'If {other} wants to race me, I’ll be there.',
    'I ain’t scared of {other}. Never have been.',
    'Me and {other}? We’ll see who blinks.',
    'I know who’s out there. Let him worry about me.',
  ],
  'rival-won': [
    'Good for {other}. I don’t want to talk about {other}.',
    '{other} won? Well, the sun came up, too.',
    'Don’t ask me about {other} today.',
    'He got one. I’ll get mine back.',
  ],
  'injury-return': [
    'I’m back and I’m not taking it easy on anybody.',
    'Doctors said take it slow. I don’t know slow.',
    'Sat on that couch long enough. Let’s go racing.',
    'Felt good to be strapped in and mad again.',
  ],
  'milestone-starts': [
    'Start number {n}. And I still want to win every one of them.',
    '{nth} start, and I’m still the same hardhead I was the first time.',
    '{n} starts, and I ain’t mellowed one bit.',
  ],
};

const ICE: Bank = {
  won: [
    'We executed. That’s the job.',
    'Good day. We’ll enjoy it tonight and go back to work Monday.',
    'The car did what we asked it to do.',
    'It came to us and we took it. Nothing more to it.',
    'Kept it clean, kept it smooth, and it worked out.',
    'You take care of the car and the car takes care of you.',
    'Nice to win. There’s another one next week.',
  ],
  'won-first': [
    'First one. I’m sure it’ll sink in later.',
    'You work a long time for one of these. It’s a good feeling.',
    'I tried not to think about it until it was over.',
    'Quiet guy, big day. I’ll take it.',
    'Nice to get that first one out of the way.',
  ],
  'won-milestone': [
    'Number {n}. It’s a nice number.',
    '{nth} win. I don’t keep count, but my crew chief does.',
    'Good to get to {n}. On to the next.',
  ],
  second: [
    'Second’s a good day. {other} was a little better.',
    'We were close. We’ll look at the tape and find the difference.',
    'Solid points. Nothing to hang our heads about.',
    'Didn’t quite have it at the end. That’s racing.',
    '{other} did a good job. We’ll be back.',
  ],
  'good-run': [
    'Good, clean day. That’s what you want.',
    'Solid points. We’ll build on it.',
    'Car was decent. We made the most of it.',
    'That’s the kind of day that wins championships over time.',
    'No complaints. We’ll keep chipping away.',
  ],
  'bad-day': [
    'Not our day. We’ll regroup.',
    'Some days you just don’t have it.',
    'We’ll go back and figure out what we missed.',
    'Long day. Next week’s a new one.',
    'You learn more from days like this than the good ones.',
  ],
  'wrecked-by': [
    'We’ll look at the tape.',
    'Hard to say from the cockpit. I’ll watch it back.',
    'It happens. I’m sure {other} didn’t mean to wreck us.',
    'Disappointing for the guys. They built a good car.',
    'I’ll talk to {other} privately. No sense doing it here.',
    'It is what it is. We move on.',
  ],
  wrecked: [
    'Hate it for {other} and his team. That’s racing.',
    'Wasn’t intentional. I’ll talk to him.',
    'Two guys going for the same spot. Somebody comes out second best.',
    'I’ll look at the replay. Didn’t mean for it to end like that.',
    'Not how I wanted it to go. I’ll make it right with him.',
  ],
  'lost-lead-late': [
    'We had a shot. It didn’t work out at the end.',
    'Stings a little. We’ll learn from it.',
    'You can’t win them all. That one hurts, though.',
    'Good run. Wrong finish.',
    'We’ll study it and be better for it.',
  ],
  'dnf-mechanical': [
    'Parts break. Nothing you can do about it from the seat.',
    'The guys will find out what happened.',
    'Out of our hands. We’ll come back stronger.',
    'Frustrating, but that’s part of the sport.',
    'We had a good car until we didn’t.',
  ],
  clinched: [
    'That was the goal. Now the real work starts.',
    'Good to get it done. We’ll keep our heads down.',
    'Proud of everybody. One thing at a time.',
    'Job’s not finished yet.',
  ],
  eliminated: [
    'Disappointing. We’ll look at the whole year and get better.',
    'Didn’t get it done. Credit to the guys who did.',
    'We came up short. It happens.',
    'Not the result we wanted. We’ll regroup over the winter.',
  ],
  'rival-nearby': [
    'I don’t worry about {other}. I worry about my car.',
    '{other} races his race, I race mine.',
    'Every car out there is a threat. {other}’s one of them.',
    'Nothing personal. Just racing.',
  ],
  'rival-won': [
    '{other} did a good job today. Credit where it’s due.',
    'Good for him. We’ll get ours.',
    'He was the best car today. We weren’t.',
    'Can’t take anything away from {other}.',
  ],
  'injury-return': [
    'Feels good to be back. We’ll ease into it.',
    'Body’s fine. Just glad to be in the car.',
    'Took the time to heal right. Ready to go.',
    'Good to be back with the guys.',
  ],
  'milestone-starts': [
    '{nth} start. Never thought about the number much.',
    '{n} starts. Grateful for every one of them.',
    'That many, huh? Time goes fast.',
  ],
};

const CORPORATE: Bank = {
  won: [
    'Great day for the {sponsor} team. Couldn’t do it without them.',
    'Want to thank {sponsor} and everybody back at the shop.',
    'Huge day for {team}. Every one of those guys earned this.',
    'Big thanks to {sponsor} — they believed in us.',
    'This one’s for the whole {team} family.',
    'Really proud of this whole organization. Great team effort.',
    'Want to thank our partners and our fans. What a day.',
  ],
  'won-first': [
    'First win, and I want to share it with everybody at {sponsor}.',
    'A first win for this whole {team} organization. Proud of every one of them.',
    'Can’t thank our partners enough for sticking with us to get here.',
    'Dreams come true. Want to thank everybody who made this possible.',
  ],
  'won-milestone': [
    'Win number {n} — and I want to thank {sponsor} for every one we’ve shared.',
    '{nth} win. Proud of this organization and our partners.',
    'Getting to {n} takes a lot of good people. Thank you all.',
  ],
  second: [
    'Good day for the {sponsor} team. {other} was just a little better.',
    'Proud of the effort from everybody at {team}.',
    'Solid run. Want to thank our partners for their support.',
    'We’ll take these points. Great job by the whole crew.',
    'Congratulations to {other}. We’ll get them next time.',
  ],
  'good-run': [
    'Good run for the {sponsor} team. We’ll build on it.',
    'Solid day for everybody at {team}.',
    'Proud of the guys. Good points for our partners.',
    'Strong effort across the board. On to next week.',
    'Want to thank our partners — good day for all of us.',
  ],
  'bad-day': [
    'Tough day, but the {sponsor} team never quits.',
    'Not the result we wanted. We’ll regroup as an organization.',
    'Our partners deserve better and we’ll give it to them.',
    'Everybody at {team} will go to work on this.',
    'We’ll learn from it. Thanks to our fans for sticking with us.',
  ],
  'wrecked-by': [
    'Unfortunate for the {sponsor} team. They gave me a great car.',
    'Hate it for our partners. We’ll regroup.',
    'Disappointing day for everybody at {team}.',
    'We’ll talk with {other} and move on.',
    'Our guys will get it fixed up. That’s what they do.',
  ],
  wrecked: [
    'Racing incident. I’ll reach out to {other} and his team.',
    'Never want to see anybody’s day end like that.',
    'Respect {other}. We’ll talk this week.',
    'Not how we race. I’ll make it right.',
    'Unfortunate for both teams. We’ll sort it out.',
  ],
  'lost-lead-late': [
    'Disappointing, but proud of the {sponsor} team.',
    'We had a shot to bring {team} a win. We’ll get it.',
    'Our partners deserved that one. Next time.',
    'Hate it for the guys. They gave me a great car.',
    'Good run for our partners, just not the finish we wanted.',
  ],
  'dnf-mechanical': [
    'The {team} guys will get to the bottom of it.',
    'Disappointing for our partners. We’ll regroup.',
    'Parts fail sometimes. This team will bounce back.',
    'Hate it for everybody at {sponsor}.',
    'Tough break. Proud of the effort, though.',
  ],
  clinched: [
    'This is for everybody at {sponsor} and {team}.',
    'Want to thank our partners — they got us here.',
    'A great accomplishment for this whole organization.',
    'Proud to deliver this for our partners and our fans.',
  ],
  eliminated: [
    'Disappointing for our partners. We gave it everything.',
    'Proud of this organization even on a day like this.',
    'We’ll regroup as a team and come back stronger.',
    'Thanks to our fans and partners for a great year.',
  ],
  'rival-nearby': [
    'Lot of respect for {other} and his team.',
    'We focus on our program. {other} focuses on his.',
    'Great competitor. Good for the sport.',
    'Our partners want to see us up front. That’s the focus.',
  ],
  'rival-won': [
    'Congratulations to {other} and his team.',
    'Hats off to {other}. Great day for them.',
    'Good for the sport. We’ll be back next week.',
    'Credit to {other}. Our {team} guys will keep working.',
  ],
  'injury-return': [
    'Want to thank our partners for their patience while I healed.',
    'Great to be back in the {sponsor} car.',
    'Thanks to the medical staff and everybody at {team}.',
    'Grateful to be back. Thanks to all our fans.',
  ],
  'milestone-starts': [
    '{nth} start. Grateful to our partners for every one.',
    '{n} starts — couldn’t do it without great people around me.',
    'A lot of starts and a lot of people to thank.',
  ],
};

const SURLY: Bank = {
  won: [
    'We won. What else do you want me to say?',
    'Car was fast. That’s it.',
    'Yeah. Good day. Can I go?',
    'You saw it. Don’t need me to tell you.',
    'Won the race. Going home.',
    'Ask the crew. They did the work.',
  ],
  'won-first': [
    'First one. Took long enough.',
    'About time.',
    'Yeah, it’s my first. You can stop asking now.',
    'Don’t make a big thing of it.',
  ],
  'won-milestone': [
    '{n}. Big deal. I want the next one.',
    'Number {n}. Write it down if you want.',
    '{nth}. Fine. Next question.',
  ],
  second: [
    'Second. Next question.',
    '{other} won. Go ask him.',
    'Didn’t win. Not happy.',
    'Nope. Not talking about second.',
    'We weren’t good enough. There’s your quote.',
  ],
  'good-run': [
    'Fine. It was fine.',
    'Ran up front. Didn’t win.',
    'Okay day. Got to go.',
    'Good enough, I guess.',
  ],
  'bad-day': [
    'No comment.',
    'Bad day. That’s all.',
    'I’m not doing this today.',
    'Ask somebody who had a good one.',
    'Leave it alone.',
  ],
  'wrecked-by': [
    'Ask {other}.',
    'You saw it. Go talk to {other}.',
    'Not talking about it. Not today.',
    'Got wrecked. You know by who.',
    'I’ll handle it myself.',
  ],
  wrecked: [
    'Racing deal. Next.',
    'He’ll live.',
    'Not my problem.',
    'Go ask {other} if you want a speech.',
    'It happens. Moving on.',
  ],
  'lost-lead-late': [
    'Had it. Lost it. Leave me alone.',
    'Not talking about the end.',
    'You saw what happened.',
    'Don’t want to relive it.',
  ],
  'dnf-mechanical': [
    'It broke.',
    'Ask the crew chief.',
    'Car quit. I didn’t.',
    'Something blew up. That’s all I know.',
  ],
  clinched: [
    'We’re in. Good.',
    'Got it done. Can we go?',
    'That was the plan.',
    'Fine. Next race.',
  ],
  eliminated: [
    'We’re out. Nothing to say.',
    'Season’s over for us. That’s it.',
    'Didn’t get it done. Done talking.',
    'No comment.',
  ],
  'rival-nearby': [
    '{other}? Don’t care.',
    'Not thinking about him.',
    'Next question.',
    'Ask him, not me.',
  ],
  'rival-won': [
    'Good for him.',
    'Don’t care who won if it wasn’t me.',
    'Next question.',
    'Ask {other} how he feels.',
  ],
  'injury-return': [
    'I’m back. Fine.',
    'Don’t ask me how I feel. I’m driving, ain’t I?',
    'Healed up. Let’s move on.',
    'Glad that’s over.',
  ],
  'milestone-starts': [
    '{n} starts. So?',
    'Lot of starts. Want more wins.',
    '{nth}. Don’t make a fuss.',
  ],
};

const QUOTABLE: Bank = {
  won: [
    'Mama always said I’d end up in victory lane or the county jail. Glad it was this one.',
    'That car was hooked up like a hound on a coon trail.',
    'I’d kiss every man on this crew if they’d hold still long enough.',
    'Hotter than a two-dollar pistol out there and twice as loud. I loved every minute.',
    'Somebody fetch me a cold drink and a phone so I can call home.',
    'I ain’t been this happy since the county fair let me drive the tractor.',
    'Went in there like a scalded cat and came out with the trophy.',
    'I’m gonna sleep with this thing tonight, and nobody can stop me.',
  ],
  'won-first': [
    'I’ve been dreaming about this since I was knee-high to a lug nut.',
    'Daddy, if you’re listening, we finally did it!',
    'Took me long enough, but the good Lord makes you wait for the sweet stuff.',
    'I’m shaking like a leaf in a hurricane. My first one!',
    'Somebody tell the folks back home to ring the church bell.',
  ],
  'won-milestone': [
    'Number {n}! I’m gonna need a bigger mantel.',
    '{nth} win, and it tastes just as sweet as the first.',
    'They say {n} is a lot. Feels like I’m just getting warmed up.',
  ],
  second: [
    'Second’s like kissing your sister — nice, but it ain’t the real thing.',
    '{other} had a little more giddy-up today, I’ll give him that.',
    'Close enough to smell it, not close enough to taste it.',
    'I was right there. Close as a June bug on a screen door.',
    'Run second and they hand you a handshake. I wanted the hardware.',
  ],
  'good-run': [
    'We were in the thick of it all day, like flies at a picnic.',
    'Good day. Not a great day, but my mama will be proud.',
    'That old car ran like a scalded dog.',
    'We’ll take it and go eat some barbecue.',
    'Felt like we were racing for the moon out there.',
  ],
  'bad-day': [
    'We were slower than molasses in January.',
    'That car handled like a shopping cart with a bad wheel.',
    'I’ve had better days fishing with no bait.',
    'Some days you’re the windshield, and today we were the bug.',
    'If it could go wrong, it went wrong and brought its cousins.',
  ],
  'wrecked-by': [
    '{other} drove into me like he was mad at my mama.',
    'I got turned around so hard I saw last week.',
    '{other} must’ve thought my bumper was his parking spot.',
    'One second I’m racing, the next I’m sightseeing in the infield.',
    'I’ll be finding pieces of that car in my sleep. Ask {other} why.',
  ],
  wrecked: [
    'Me and {other} wanted the same piece of real estate, and I don’t share.',
    'It’s a contact sport. Somebody forgot to tell {other}.',
    'We swapped some paint and he came out on the short end.',
    'I’ll send him a fruit basket. Maybe.',
    'That’s stock-car racing, buddy. Pack a lunch.',
  ],
  'lost-lead-late': [
    'Had it in my pocket and there was a hole in the pocket.',
    'We were the belle of the ball right up until midnight.',
    'Led the parade and somebody stole the float.',
    'That one’s gonna eat at me like a tick on a hound.',
    'So close I could’ve reached out and touched the trophy.',
  ],
  'dnf-mechanical': [
    'That motor gave up the ghost and took my good mood with it.',
    'She coughed, she sputtered, and she died on me.',
    'Smoke where there shouldn’t be smoke. Never a good sign.',
    'I’ve seen lawn mowers last longer than that today.',
    'We were rolling till something went bang. Then we weren’t.',
  ],
  clinched: [
    'We’re in, y’all! Somebody start the grill.',
    'Mark it down — this bunch is going dancing.',
    'Happier than a pig in sunshine right now.',
    'That’s what all those late nights in the shop were for.',
  ],
  eliminated: [
    'We got voted off the island, folks.',
    'Feels like somebody stole my dog and my truck the same day.',
    'Ain’t no sugarcoating it. We came up short.',
    'We’ll lick our wounds and be back meaner.',
  ],
  'rival-nearby': [
    'Me and {other} are like two cats in a sack. Something’s gonna give.',
    '{other} knows where I live, and I know where he parks.',
    'Every time I look in the mirror, there’s {other}. Like a bad penny.',
    'I’d tell you about {other}, but my mama might be reading.',
    'Some fellas you race, and some fellas you race twice as hard.',
  ],
  'rival-won': [
    '{other} won? Well, even a blind hog finds an acorn now and then.',
    'Tip of the cap to {other}. Through gritted teeth.',
    'Good for {other}. I’m gonna go kick a tire now.',
    'He got lucky. I hope he enjoys it, because I won’t.',
  ],
  'injury-return': [
    'Doc said I was fit as a fiddle. I feel like a fiddle somebody sat on, but I’m back.',
    'Missed this place like a hound misses his porch.',
    'Got all my parts back in the right places. Let’s race.',
    'Sat on the couch so long it knew my name.',
  ],
  'milestone-starts': [
    '{nth} start! I’ve worn out more seats than a church pew.',
    '{n} starts, and I’d do every one of them again.',
    'That’s a lot of Sundays. Wouldn’t trade a one.',
  ],
};

const PLAIN: Bank = {
  won: [
    'Good car today. The guys deserve it.',
    'Proud of this team. They worked hard for it.',
    'It feels good. Real good.',
    'We had what we needed when we needed it.',
    'I’ll remember this one.',
    'Can’t ask for more than that.',
    'Long way to get here. Worth it.',
  ],
  'won-first': [
    'My first one. Don’t really know what to say.',
    'I’ve thought about this a long time. It’s better than I imagined.',
    'Wish my family was here. They’ll hear about it.',
    'Hard to put into words.',
    'A lot of people helped me get here.',
  ],
  'won-milestone': [
    'Number {n}. That means something to me.',
    '{nth} win. Never takes away the feeling.',
    'Didn’t think I’d ever get to {n}.',
  ],
  second: [
    'We were close. {other} was better.',
    'Good run. Just came up short.',
    'Second is fine. I wanted first.',
    'Gave it all we had.',
    'Not bad. Not what we wanted, either.',
  ],
  'good-run': [
    'Good day. We’ll take it.',
    'Car was strong. Good points.',
    'Happy with that.',
    'We ran well. Something to build on.',
    'Clean day. That’s all you can ask.',
  ],
  'bad-day': [
    'Tough day.',
    'We weren’t very good.',
    'Not much to say about that one.',
    'We’ll be better.',
    'Long afternoon.',
  ],
  'wrecked-by': [
    'Got wrecked. Not much I could do.',
    '{other} got into us. Frustrating.',
    'Bad deal. Hate it for the guys.',
    'Was a good car till then.',
    'Not happy about it.',
  ],
  wrecked: [
    'Racing deal. Didn’t mean for it to happen.',
    'Tough spot. Hate it for {other}.',
    'We were both going for it.',
    'I’ll talk to him.',
    'Not how I wanted that to go.',
  ],
  'lost-lead-late': [
    'That one hurts.',
    'We had a shot and it got away.',
    'Close. Just not close enough.',
    'Hard way to end it.',
    'We’ll get another chance.',
  ],
  'dnf-mechanical': [
    'Something let go. Nothing I could do.',
    'Car quit on us.',
    'Bad luck.',
    'Disappointing. We had a decent car.',
    'The guys will figure it out.',
  ],
  clinched: [
    'Good feeling. We earned it.',
    'That’s what we were working for.',
    'Proud of the team.',
    'Glad to get it done.',
  ],
  eliminated: [
    'We didn’t get it done.',
    'Disappointing. Nothing else to say.',
    'Came up short this time.',
    'We’ll be back.',
  ],
  'rival-nearby': [
    'I know where {other} is.',
    'Just going to race my race.',
    '{other} is fast. So are we.',
    'We’ll see how it plays out.',
  ],
  'rival-won': [
    'Good for {other}.',
    'He had a good day. We didn’t.',
    'Credit to him.',
    'We’ll get ours.',
  ],
  'injury-return': [
    'Good to be back.',
    'Feels right to be in the car again.',
    'Missed this.',
    'Ready to go.',
  ],
  'milestone-starts': [
    '{nth} start. That’s a lot of racing.',
    '{n} starts. I’m thankful.',
    'Never thought I’d get to that many.',
  ],
};

export const VOICE_BANK: Readonly<Record<Voice, Bank>> = {
  hothead: HOTHEAD,
  ice: ICE,
  corporate: CORPORATE,
  surly: SURLY,
  quotable: QUOTABLE,
  plain: PLAIN,
};

/** Short add-ons by mood. Never a name, never a number. */
export const TAGS: Readonly<Record<Voice, Record<Mood, readonly string[]>>> = {
  hothead: {
    up: ['Put that in your paper.', 'And I ain’t done.', 'Y’all can quote me.', 'That’s how you do it.', 'Now let’s go do it again.'],
    down: ['I’m not gonna forget it.', 'Write that down.', 'Somebody’s gonna hear about this.', 'I’m hot, and I’ve got a right to be.', 'This ain’t over.'],
    mixed: ['That’s all I’ve got to say.', 'Take it how you want it.', 'I said what I said.', 'Don’t like it? Too bad.'],
  },
  ice: {
    up: ['On to the next one.', 'We’ll keep our heads down.', 'Back to work Monday.', 'One week at a time.'],
    down: ['We’ll regroup.', 'Nothing we can do about it now.', 'We move on.', 'Plenty of racing left.'],
    mixed: ['That’s racing.', 'We’ll learn from it.', 'Keep it in perspective.', 'Next week.'],
  },
  corporate: {
    up: ['Thanks to all our fans.', 'Great team effort.', 'Proud of everybody back at the shop.', 'Couldn’t do it without our partners.'],
    down: ['We’ll regroup as a team.', 'Thanks to our fans for their support.', 'We’ll be back stronger.', 'Proud of the effort regardless.'],
    mixed: ['Great effort by the whole organization.', 'On to next week.', 'Thanks to our partners.', 'Good for the sport.'],
  },
  surly: {
    up: ['That’s it.', 'Done.', 'Can I go now?'],
    down: ['Done talking.', 'No more questions.', 'Leave it.'],
    mixed: ['That’s all.', 'Next.', 'Whatever.'],
  },
  quotable: {
    up: ['Somebody pass the sweet tea.', 'Tell my mama I said hey.', 'Yee-haw, y’all.', 'Grin’s gonna be stuck on my face a week.'],
    down: ['Bless its heart.', 'Lord, give me strength.', 'I need a cold drink and a long nap.', 'We’ll be back meaner than a wet hornet.'],
    mixed: ['That’s racing, buddy.', 'You can’t make this stuff up.', 'Ain’t that something.', 'Funny old game.'],
  },
  plain: {
    up: ['Good day.', 'Proud of everybody.', 'We’ll keep going.'],
    down: ['We’ll be back.', 'On to the next one.', 'That’s how it goes.'],
    mixed: ['That’s racing.', 'We’ll see.', 'Moving on.'],
  },
};

/**
 * Used only when the rivalry book shows the two men had history BEFORE this
 * race — the line then states a fact ("not the first time").
 */
export const RIVAL_TAGS: Readonly<Record<Voice, readonly string[]>> = {
  hothead: ['Me and him have history, and it just got longer.', 'Not the first time with him. Won’t be the last.', 'He knows exactly what this is about.', 'This has been coming a while.'],
  ice: ['Not the first time we’ve been in each other’s way.', 'We’ve got some history. I’ll leave it there.', 'We’ll sort it out between us, like before.'],
  corporate: ['We’ve had our moments, but there’s respect there.', 'We’ve raced each other hard before. That won’t change.', 'Lot of history there. We’ll handle it professionally.'],
  surly: ['Not the first time.', 'He knows.', 'Same old thing with him.'],
  quotable: ['Me and him go together like a cat and a bathtub.', 'We’ve been at it so long we should exchange Christmas cards.', 'Him again. Like a bad rash, he keeps coming back.'],
  plain: ['We’ve got some history.', 'Not the first time.', 'It’s been building.'],
};
